import axios from 'axios'
import pako from 'pako'
import { buildApiUrl } from '../../utils/formatHelpers'
import { apiConfigService } from '../apiConfigService'
import { clearSession } from '../../utils/userSessions'

// Build API URLs considering any overrides
const buildClientUrls = () => {
    // Check if override is in effect
    const override = apiConfigService.getApiOverride()
    if (apiConfigService.shouldUseOverride()) {
        const stage = apiConfigService.getEffectiveStage()
        const domain = apiConfigService.getEffectiveDomain()
        console.log(`🔄 API Override Active: ${override} (stage=${stage}, domain=${domain})`)
    } else {
        console.log('📍 Using default API configuration')
    }

    const urls = {
        identityApiUrl: buildApiUrl('identity-api', 'https', '/v1'),
        contentApiUrl: buildApiUrl('content-api', 'https', '/v1'),
        modelApiUrl: buildApiUrl('model-api', 'https', '/v1'),
        domainApiUrl: buildApiUrl('domain-api', 'https', '/v1'),
    } as const

    // Log the actual URLs being used
    console.log('API URLs configured:', {
        identity: urls.identityApiUrl,
        content: urls.contentApiUrl,
        model: urls.modelApiUrl,
        domain: urls.domainApiUrl,
    })

    return urls
}

export const API_CLIENT_URLS = buildClientUrls()

const createAuthenticatedInstance = (
    baseURL: string,
    getValidToken: (forceRefresh?: boolean) => Promise<string | null>
) => {
    const api = axios.create({
        baseURL,
        headers: {
            'Content-Type': 'application/json',
        },
    })

    api.interceptors.request.use(async (config) => {
        const token = await getValidToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }

        // Compress request body if it's large enough (>1KB) and it's a POST/PUT/PATCH request
        if (config.data && ['post', 'put', 'patch'].includes(config.method?.toLowerCase() || '')) {
            const dataString = typeof config.data === 'string' ? config.data : JSON.stringify(config.data)

            // Only compress if data is larger than 1KB
            if (dataString.length > 1024) {
                // Compress the string data
                const compressed = pako.gzip(dataString)

                // Set the compressed data
                config.data = compressed

                // Add Content-Encoding header
                config.headers['Content-Encoding'] = 'gzip'

                // IMPORTANT: Keep Content-Type as JSON - API Gateway will decompress
                // before passing to Lambda, which expects JSON
                config.headers['Content-Type'] = 'application/json'

                // Tell axios to handle this as binary data
                config.transformRequest = [(data) => data]
            }
        }

        return config
    })

    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config

            // If we get a 401 and haven't retried yet, try to refresh the token
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true

                try {
                    console.log('[API Client] Got 401, forcing token refresh...')
                    // Force refresh the token since we got a 401
                    const newToken = await getValidToken(true) // Pass true to force refresh

                    if (newToken) {
                        // Update the authorization header
                        originalRequest.headers.Authorization = `Bearer ${newToken}`
                        console.log('[API Client] Retrying request with new token...')
                        // Retry the original request with the new token
                        return api(originalRequest)
                    } else {
                        console.error(
                            '[API Client] Token refresh returned null, clearing tokens and redirecting to login'
                        )
                        // Clear all auth state before redirecting to login
                        clearSession()
                        // Redirect to login after a short delay
                        setTimeout(() => {
                            window.location.href = '/login'
                        }, 1000)
                        return Promise.reject(new Error('Authentication failed - redirecting to login'))
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed during 401 retry:', refreshError)
                    // Clear all auth state before redirecting to login
                    clearSession()
                    setTimeout(() => {
                        window.location.href = '/login'
                    }, 1000)
                    return Promise.reject(refreshError)
                }
            }

            console.error('API Error:', {
                url: error.config?.url,
                status: error.response?.status,
                data: error.response?.data,
                message: error.message,
            })
            return Promise.reject(error)
        }
    )

    return api
}

export const createApiClient = (getValidToken: (forceRefresh?: boolean) => Promise<string | null>) => {
    const { identityApiUrl, contentApiUrl, modelApiUrl, domainApiUrl } = API_CLIENT_URLS
    return {
        identityApi: createAuthenticatedInstance(identityApiUrl, getValidToken),
        contentApi: createAuthenticatedInstance(contentApiUrl, getValidToken),
        modelApi: createAuthenticatedInstance(modelApiUrl, getValidToken),
        domainApi: createAuthenticatedInstance(domainApiUrl, getValidToken),
    }
}
